import React, { useEffect, useState } from 'react';
import { Router } from 'react-router-dom';
import { createBrowserHistory } from 'history';
import { create } from 'jss';
import rtl from 'jss-rtl';
import { SnackbarProvider, useSnackbar } from 'notistack';
import { StyledEngineProvider } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import jssPreset from '@mui/styles/jssPreset';
import makeStyles from '@mui/styles/makeStyles';
import StylesProvider from '@mui/styles/StylesProvider';
import Auth from 'src/components/Auth';
import ScrollReset from 'src/components/ScrollReset';
import useSettings from 'src/hooks/useSettings';
import { createTheme } from 'src/theme';
import Routes from 'src/Routes';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers';
import moment from 'moment';
import i18n from './i18n';
import 'moment/locale/is';
import 'moment/locale/en-gb';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import * as serviceWorker from 'src/serviceWorker';

Bugsnag.start({
  enabledReleaseStages: ['staging', 'production'],
  apiKey: '1b464f161a3458c09255cb7fb6db81e9',
  plugins: [new BugsnagPluginReact()],
  onError: (event) => {
    const original = event.originalError;
    const errorClass = event.errors?.[0]?.errorClass;
    const message = event.errors?.[0]?.errorMessage || original?.message || '';

    const status =
      event.errors?.[0]?.errorClass === 'HttpError'
        ? event.errors?.[0]?.errorMessage
        : event.getMetadata('request', 'status') ||
          event.getMetadata('response', 'status') ||
          event.originalError?.status ||
          event.originalError?.response?.status;

    if (status === 401 || status === 404) {
      event.severity = 'info';
    }

    const isChunkLoadError =
      original?.name === 'ChunkLoadError' || errorClass === 'ChunkLoadError' || /Loading chunk \d+ failed/i.test(message) || /chunk.*failed/i.test(message);

    if (isChunkLoadError) {
      return false;
    }
  }
});

const ErrorBoundary = Bugsnag.getPlugin('react').createErrorBoundary(React);

const history = createBrowserHistory();
const jss = create({ plugins: [...jssPreset().plugins, rtl()] });

const useStyles = makeStyles(() =>
  createStyles({
    '@global': {
      '*': {
        boxSizing: 'border-box',
        margin: 0,
        padding: 0
      },
      html: {
        '-webkit-font-smoothing': 'antialiased',
        '-moz-osx-font-smoothing': 'grayscale',
        height: '100%',
        width: '100%'
      },
      body: {
        height: '100%',
        width: '100%'
      },
      '#root': {
        height: '100%',
        width: '100%'
      },
      'fieldset:focused': {
        borderColor: 'red'
      }
    }
  })
);

function App({ t }) {
  useStyles();

  const { settings } = useSettings();

  i18n.changeLanguage(settings.language);

  moment.locale(settings.language);

  const [newVersionAvailable, setNewVersionAvailable] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState(null);
  const { enqueueSnackbar } = useSnackbar();

  const onServiceWorkerUpdate = (registration) => {
    setWaitingWorker(registration && registration.waiting);
    setNewVersionAvailable(true);
  };

  const updateServiceWorker = () => {
    if (waitingWorker) {
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
      setNewVersionAvailable(false);
      window.location.reload();
    }
  };

  const refreshAction = () => {
    return (
      <Fragment>
        <Button className="snackbar-button" size="small" onClick={updateServiceWorker}>
          {'Uppfæra'}
        </Button>
      </Fragment>
    );
  };

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') {
      serviceWorker.register({ onUpdate: onServiceWorkerUpdate });
    }

    if (newVersionAvailable) {
      enqueueSnackbar('Ný uppfærsla er tilbúin', {
        persist: true,
        variant: 'success',
        action: refreshAction()
      });
    }
  }, [newVersionAvailable, enqueueSnackbar]);

  return (
    <ErrorBoundary>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={createTheme(settings)}>
          <StylesProvider jss={jss}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <SnackbarProvider maxSnack={1}>
                <Router history={history}>
                  <Auth>
                    <ScrollReset />
                    <Routes />
                  </Auth>
                </Router>
              </SnackbarProvider>
            </LocalizationProvider>
          </StylesProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    </ErrorBoundary>
  );
}

export default App;
